import PropTypes from "prop-types"
import React, {forwardRef, useImperativeHandle, useMemo, useRef, useState} from "react"
import {Badge, Col, Modal, ModalBody, ModalHeader, Row, Spinner} from "reactstrap"
import {
    apiAlterarVisibilidadeGrupoCardapio,
    apiBuscarFileGrupoCardapio,
    apiBuscarGruposCardapio,
    apiMoverGrupoCardapio
} from "../../helpers/backend_helper"
import ListDataTable from "../../components/Common/list-data-table"
import {Link} from "react-router-dom"
import {AvForm} from "availity-reactstrap-validation"
import {Box, TextField} from "@mui/material"
import {describeDateTime, removeHour} from "../../helpers/utils"
import {LocalizationProvider} from "@mui/x-date-pickers/LocalizationProvider"
import {AdapterDateFns} from "@mui/x-date-pickers/AdapterDateFns"
import "react-datepicker/dist/react-datepicker.css"
import {DatePicker} from "@mui/x-date-pickers/DatePicker"
import ptBR from "date-fns/locale/pt-BR"
import {ptBR as ptBRDatePickers} from '@mui/x-date-pickers'
import {registerLocale, setDefaultLocale} from "react-datepicker"
import {useTranslation} from "react-i18next"
import toastr from "toastr"
import "toastr/build/toastr.min.css"
const ptBRLocale = ptBRDatePickers.components.MuiLocalizationProvider.defaultProps.localeText

registerLocale("ptBR", ptBR)
setDefaultLocale("ptBR")

const COLUNAS_VISIBILIDADE = [
    { campo: 'Desktop_Visivel', chave: 'desktopVisivel' },
    { campo: 'Mobile_Visivel', chave: 'mobileVisivel' },
    { campo: 'Infinitifood_Visivel', chave: 'infinitifoodVisivel' },
    { campo: 'MenuDigital_Visivel', chave: 'menuDigitalVisivel' }
]

// Nas colunas de visibilidade o valor nulo equivale a visível.
const estaVisivel = valor => valor !== 'N'

const ehSim = valor => valor === 'S'

// Categoria que veio do Fastfood local: no InfoCloud só a Ordem dela pode ser alterada.
const ehSincronizada = categoria => !!categoria?.iCodigo

const ordenacaoPelaOrdem = ordenacao => !Array.isArray(ordenacao) || ordenacao.length === 0 ||
    (ordenacao[0].id === 'Ordem' && !ordenacao[0].desc)

const descreverErro = (error, mensagemPadrao) => {
    const retorno = error?.response?.data
    if (typeof retorno === 'string') return retorno
    return retorno?.message || retorno?.error || mensagemPadrao
}

const GruposTable = forwardRef((props, refFather) => {

    const ref = useRef()
    const { t } = useTranslation()

    const { filters, columns, multiplasEmpresas, usePermissionRemove, usePermissionEdit, removerGrupoCardapio, navegarEditarGrupoCardapio } = props

    const [data, setData] = useState([])
    const [modalRemove, setModalRemove] = useState(false)
    const [itemSelecionado, setItemSelecionado] = useState()
    const [imagemModal, setImagemModal] = useState({ aberto: false, url: null, titulo: '', carregando: false })
    // Enquanto uma movimentação está em andamento todas as setas ficam bloqueadas: a troca muda as
    // vizinhas e o grid é recarregado em seguida.
    const [movendo, setMovendo] = useState(false)
    // As setas só fazem sentido com o grid na ordem das categorias (a padrão, ou pela coluna Ordem).
    const [ordenadoPorOrdem, setOrdenadoPorOrdem] = useState(true)
    const imagemSolicitada = useRef(0)

    useImperativeHandle(refFather, () => ({
        search() {
            ref?.current?.search()
        }
    }))

    const showConfirmDelete = item => {
        setItemSelecionado(item)
        setModalRemove(true)
    }

    const remover = () => {
        removerGrupoCardapio(itemSelecionado)
        setModalRemove(false)
    }

    const aplicarAlteracaoLocal = (id, alteracao) => {
        setData(linhas => linhas.map(linha => linha.id === id ? { ...linha, ...alteracao } : linha))
    }

    const alternarVisibilidade = async (categoria, campo) => {
        const valorAnterior = categoria[campo]
        const novoValor = estaVisivel(valorAnterior) ? 'N' : 'S'
        const pendente = `pendente_${campo}`
        if (categoria[pendente]) return

        aplicarAlteracaoLocal(categoria.id, { [campo]: novoValor, [pendente]: true })

        try {
            const atualizado = await apiAlterarVisibilidadeGrupoCardapio({ id: categoria.id, campo, valor: novoValor })
            aplicarAlteracaoLocal(categoria.id, {
                [campo]: novoValor,
                [pendente]: false,
                sincronizar: atualizado?.sincronizar || 'S',
                ...(atualizado?.AltData ? { AltData: atualizado.AltData } : {})
            })
            toastr.success(t("grupocardapio.grid.visibilidade.sucesso"))
        } catch (error) {
            aplicarAlteracaoLocal(categoria.id, { [campo]: valorAnterior, [pendente]: false })
            toastr.error(descreverErro(error, t("grupocardapio.grid.visibilidade.erro")))
        }
    }

    const mover = async (categoria, direcao) => {
        if (movendo) return
        setMovendo(true)
        try {
            await apiMoverGrupoCardapio({ id: categoria.id, direcao })
            toastr.success(t("grupocardapio.grid.ordem.sucesso"))
            ref?.current?.search()
        } catch (error) {
            toastr.error(descreverErro(error, t("grupocardapio.grid.ordem.erro")))
        } finally {
            setMovendo(false)
        }
    }

    const abrirImagem = async (categoria) => {
        const solicitacao = ++imagemSolicitada.current
        setImagemModal({ aberto: true, url: null, titulo: categoria.Descricao, carregando: true })
        try {
            const arquivo = await apiBuscarFileGrupoCardapio({ id: categoria.id })
            // O modal foi fechado (ou outra imagem pedida) antes de a resposta chegar.
            if (solicitacao !== imagemSolicitada.current) return
            setImagemModal(atual => ({ ...atual, url: URL.createObjectURL(arquivo), carregando: false }))
        } catch (error) {
            if (solicitacao !== imagemSolicitada.current) return
            setImagemModal({ aberto: false, url: null, titulo: '', carregando: false })
            toastr.error(descreverErro(error, t("grupocardapio.grid.imagem.erro")))
        }
    }

    const fecharImagem = () => {
        imagemSolicitada.current++
        setImagemModal(atual => {
            if (atual.url) URL.revokeObjectURL(atual.url)
            return { aberto: false, url: null, titulo: '', carregando: false }
        })
    }

    const renderBadgeSimNao = (sim) => (
        <Badge
            className={"font-size-12 badge-soft-" + (sim ? 'primary higher' : 'soft-dark')}
            color={sim ? 'primary higher' : 'soft-dark'}
            pill
        >
            {sim ? t("grupocardapio.grid.sim") : t("grupocardapio.grid.nao")}
        </Badge>
    )

    const renderCelulaVisibilidade = (categoria, campo) => {
        const visivel = estaVisivel(categoria[campo])
        if (!usePermissionEdit) {
            return <Box sx={{display: 'flex', alignItems: 'center', gap: '1rem'}}>{renderBadgeSimNao(visivel)}</Box>
        }
        if (ehSincronizada(categoria)) {
            return (
                <Box sx={{display: 'flex', alignItems: 'center', gap: '1rem'}}
                     title={t("grupocardapio.grid.visibilidade.sincronizada")}>
                    {renderBadgeSimNao(visivel)}
                </Box>)
        }

        return (
            <Box sx={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                {renderBadgeSimNao(visivel)}
                <button type="button"
                        className="btn btn-link btn-sm p-0 text-primary"
                        title={t("grupocardapio.grid.visibilidade.alternar")}
                        disabled={!!categoria[`pendente_${campo}`]}
                        onClick={() => alternarVisibilidade(categoria, campo)}>
                    <i className="mdi mdi-swap-horizontal font-size-18"/>
                </button>
            </Box>
        )
    }

    const colunasVisibilidade = () => COLUNAS_VISIBILIDADE.map(({ campo, chave }) => ({
        header: t(`grupocardapio.grid.coluna.${chave}`),
        accessorKey: campo,
        size: 190,
        filterVariant: 'checkbox',
        Cell: ({row}) => renderCelulaVisibilidade(row.original, campo)
    }))

    const colunas = useMemo(() => columns ||
        [
            { header: t("grupocardapio.grid.coluna.ordem"), accessorKey: "Ordem", size: 130,
                Cell: ({row}) => (
                    <Box sx={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}
                         title={ordenadoPorOrdem ? undefined : t("grupocardapio.grid.ordem.ordenarPorOrdem")}>
                        <span>{row.original.Ordem ?? '-'}</span>
                        {usePermissionEdit && <>
                            <button type="button"
                                    className="btn btn-link btn-sm p-0 text-primary"
                                    title={t("grupocardapio.grid.ordem.subir")}
                                    disabled={movendo || !ordenadoPorOrdem || !!row.original.primeiroDaEmpresa}
                                    onClick={() => mover(row.original, 'subir')}>
                                <i className="mdi mdi-arrow-up-bold font-size-18"/>
                            </button>
                            <button type="button"
                                    className="btn btn-link btn-sm p-0 text-primary"
                                    title={t("grupocardapio.grid.ordem.descer")}
                                    disabled={movendo || !ordenadoPorOrdem || !!row.original.ultimoDaEmpresa}
                                    onClick={() => mover(row.original, 'descer')}>
                                <i className="mdi mdi-arrow-down-bold font-size-18"/>
                            </button>
                        </>}
                    </Box>),
            },
            { header: t("grupocardapio.grid.coluna.idCloud"), accessorKey: "id", size: 80, enableHiding: false,
                muiTableBodyCellProps: {
                    align: 'right',
                },
            },
            { header: t("grupocardapio.grid.coluna.empresa"), accessorKey: "empresa.Fantasia", size: 180, filterFn: 'contains',
                accessorFn: (row) => row.empresa?.Fantasia || ''
            },
            { header: t("grupocardapio.grid.coluna.codigo"), accessorKey: "Codigo", size: 80},
            { header: t("grupocardapio.grid.coluna.descricao"), accessorKey: "Descricao", size: 120, filterFn: 'contains'},
            { header: t("grupocardapio.grid.coluna.descricaoEnUs"), accessorKey: "Descricao_EN_US", size: 180, filterFn: 'contains'},
            { header: t("grupocardapio.grid.coluna.descricaoEs"), accessorKey: "Descricao_ES", size: 180, filterFn: 'contains'},
            { header: t("grupocardapio.grid.coluna.subgrupo"), accessorKey: "SubGrupo", size: 90,
                filterVariant: 'checkbox',
                Cell: ({cell}) => (
                    <Box sx={{display: 'flex', alignItems: 'center', gap: '1rem',}}>
                        {renderBadgeSimNao(ehSim(cell.getValue()))}
                    </Box>),
            },
            ...colunasVisibilidade(),
            { header: t("grupocardapio.grid.coluna.imagem"), accessorKey: "possuiImagem", size: 100,
                enableColumnFilter: false, enableSorting: false,
                Cell: ({row}) => (
                    row.original.possuiImagem
                        ? <button type="button"
                                  className="btn btn-link btn-sm p-0 text-primary"
                                  title={t("grupocardapio.grid.imagem.abrir")}
                                  onClick={() => abrirImagem(row.original)}>
                            <i className="mdi mdi-image font-size-20"/>
                        </button>
                        : <span className="text-muted">{t("grupocardapio.grid.imagem.semImagem")}</span>),
            },
            { header: t("grupocardapio.grid.coluna.monitorKds"), accessorKey: "monitor_kds", size: 130,
                muiTableBodyCellProps: {
                    align: 'right',
                },
                // Zero ou nulo significa que a categoria não é exibida em nenhum monitor KDS.
                Cell: ({cell}) => cell.getValue() > 0
                    ? cell.getValue()
                    : <span className="text-muted">{t("grupocardapio.grid.monitorKds.naoExibe")}</span>,
            },
            { header: t("grupocardapio.grid.coluna.sincronizar"), accessorKey: "sincronizar", size: 200,
                filterVariant: 'checkbox',
                Cell: ({cell}) => (
                    <Box sx={{display: 'flex', alignItems: 'center', gap: '1rem',}}>
                        {renderBadgeSimNao(ehSim(cell.getValue()))}
                    </Box>),
            },
            { header: t("grupocardapio.grid.coluna.qtdeItens"), accessorKey: "itensCount", size: 90, enableColumnFilter: false,},
            { header: t("grupocardapio.grid.coluna.qtdeObservacoes"), accessorKey: "observacoesCount", size: 180, enableColumnFilter: false,},
            { header: t("grupocardapio.grid.coluna.ultimaAlteracao"), size: 180,
                Cell: ({cell}) => describeDateTime(cell?.getValue()),
                accessorFn: (row) => new Date(row.AltData),
                accessorKey: 'AltData',
                sortingFn: 'datetime',
                Filter: ({column}) => (
                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR} localeText={ptBRLocale}>
                        <DatePicker
                            onChange={(newValue) => {
                                if (newValue) {
                                    newValue = removeHour(newValue, false)
                                }
                                column.setFilterValue(newValue)
                            }}
                            inputFormat="dd/MM/yyyy"
                            componentsProps={{
                                actionBar: {
                                    actions: ['accept', 'clear'],
                                },
                            }}
                            value={column.getFilterValue() ? new Date(column.getFilterValue()) : null}
                            views={['year', 'month', 'day']}
                            renderInput={(props) => <TextField {...props} variant="standard"/>}
                        />
                    </LocalizationProvider>
                ),
            },
        ], [columns, usePermissionEdit, movendo, ordenadoPorOrdem])

    return (
        <React.Fragment>
            <ListDataTable
                ref={ref}
                searchFn={params => apiBuscarGruposCardapio(params)}
                advancedFilters={filters}
                columns={colunas}
                data={data}
                setData={setData}
                columnVisibility={{
                    'empresa.Fantasia': multiplasEmpresas,
                    SubGrupo: false,
                    Descricao_EN_US: false,
                    Descricao_ES: false,
                    sincronizar: false
                }}
                enableRowActions={true}
                // Mesmo desempate do servidor (Ordem e depois id crescentes): é por ele que o Subir/Descer
                // decide quem é a vizinha e qual é a primeira e a última categoria da empresa.
                defaultSorting={[{
                    id: 'Ordem',
                    desc: false,
                }, {
                    id: 'id',
                    desc: false,
                }]}
                onSortingStateChange={ordenacao => setOrdenadoPorOrdem(ordenacaoPelaOrdem(ordenacao))}
                renderRowActions={(row, table) =>
                    (<Box sx={{display: 'flex', gap: '1rem'}}>
                        <Link to="#" className="text-primary">
                            <i className="mdi mdi-eye font-size-18 ms-1" id="viewtooltip"
                               onClick={e => navegarEditarGrupoCardapio(row.original)}
                               title={t("grupocardapio.grid.acao.visualizar")}
                            />
                        </Link>
                        {usePermissionEdit && <Link to="#" className="text-primary">
                            <i className="mdi mdi-pencil font-size-18 ms-1" id="edittooltip"
                                onClick={e => navegarEditarGrupoCardapio(row.original)}
                               title={t("grupocardapio.grid.acao.editar")}
                            />
                        </Link>}
                        {usePermissionRemove && <Link to="#" className="text-danger">
                            <i className="mdi mdi-delete font-size-18" id="btnDelete"
                               onClick={() => showConfirmDelete(row.original)}
                               title={t("grupocardapio.grid.acao.remover")}/>
                        </Link>}
                    </Box>)
                }
            />
            <Modal isOpen={imagemModal.aberto} toggle={fecharImagem} centered>
                <ModalHeader tag="h4" toggle={fecharImagem}>
                    {imagemModal.titulo || t("grupocardapio.grid.imagem.modal.titulo")}
                </ModalHeader>
                <ModalBody className="text-center">
                    {imagemModal.carregando
                        ? <>
                            <Spinner color="primary"/>
                            <div className="mt-2 text-muted">{t("grupocardapio.grid.imagem.carregando")}</div>
                        </>
                        : imagemModal.url && <img src={imagemModal.url}
                                                  alt={imagemModal.titulo}
                                                  className="img-fluid rounded"/>}
                </ModalBody>
            </Modal>
            <Modal isOpen={modalRemove}>
                <ModalHeader tag="h4">{t("grupocardapio.grid.remover.titulo")}</ModalHeader>
                <ModalBody>
                    <AvForm onValidSubmit={(e, v) => {
                        remover()
                    }}>
                        <Row form>
                            <Col className="col-12">
                                {t("grupocardapio.grid.remover.mensagem")}
                            </Col>
                        </Row>
                        <Row style={{paddingTop: 10}}>
                            <Col>
                                <div className="text-right">
                                    <button type="submit" className="btn btn-success save-event">
                                        {t("grupocardapio.grid.remover.confirmar")}
                                    </button>
                                    <button type="button" className="btn btn-light ms-2"
                                            onClick={e => setModalRemove(false)}>
                                        {t("grupocardapio.grid.remover.cancelar")}
                                    </button>
                                </div>
                            </Col>
                        </Row>
                    </AvForm>
                </ModalBody>
            </Modal>
        </React.Fragment>
    )
})

export default GruposTable

GruposTable.propTypes = {
    filters: PropTypes.array,
    columns: PropTypes.array,
    multiplasEmpresas: PropTypes.bool,
    usePermissionRemove: PropTypes.bool,
    usePermissionEdit: PropTypes.bool,
    history: PropTypes.any,
    navegarEditarGrupoCardapio: PropTypes.func,
    removerGrupoCardapio: PropTypes.func,
}
